import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Segment, Popup, Icon } from 'semantic-ui-react';
import { CustomTooltip } from '../../../components';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { putActivity } from '../../../store/actions';
import { get, isEmpty } from '../../../utils/lodash';
import { isParentProgramActivity } from '../../../utils/activity';
import { withTranslation } from 'react-i18next';
import { HtmlService } from '../../../services';
import {
  CKField,
  IncludeInReportToggle,
  saveReportInclusion,
} from './reportFields';
import './activity.scss';

function DocumentNotesBase({
  currentActivity,
  putActivity,
  user,
  helpTips,
  pageDisabled,
}) {
  const { t } = useTranslation();
  const { evaluationPlans = [], isAnalysisField } = currentActivity;
  const normalEvaluation = evaluationPlans.filter(
    (item) => item.methodId !== 9
  );
  const isArchived = currentActivity.statusId == 3; // eslint-disable-line eqeqeq
  const fieldDisabled = isArchived || pageDisabled;

  const showAnalysis =
    isAnalysisField &&
    (isParentProgramActivity(currentActivity.activityPlanTypeId) ||
      !isEmpty(normalEvaluation));

  const showNotes = !(user.role === 'guest' && currentActivity.parentId);

  // reportInclusions — stored on sProject.reportInclusions (JSON).
  // Parse once from currentActivity; updated locally on toggle click.
  const parseInclusions = (raw) => {
    try {
      return JSON.parse(raw || '{}');
    } catch {
      return {};
    }
  };
  const [inclusions, setInclusions] = useState(() =>
    parseInclusions(currentActivity.reportInclusions)
  );

  useEffect(() => {
    setInclusions(parseInclusions(currentActivity.reportInclusions));
  }, [currentActivity.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInclusionToggle = useCallback(
    async (key, value) => {
      setInclusions((prev) => ({ ...prev, [key]: value }));
      const merged = await saveReportInclusion({
        activityId: currentActivity.id,
        key,
        value,
      });
      if (merged) {
        setInclusions(merged);
      }
    },
    [currentActivity.id]
  );

  return (
    <Segment className="document-note-seg" disabled={pageDisabled}>
      <div className="document-note-div">
        {showAnalysis && (
          <CKField
            key={`analysis-${currentActivity.id}-${
              currentActivity?.analysis || ''
            }`}
            label={
              <CustomTooltip
                state={get(user, 'options.tips')}
                contentObject={{ data: helpTips, key: '219' }}
                position="top left"
                wide="very"
              >
                <div>
                  {t(
                    'activity.documentation.analysis_and_reflection.evaluationResultsAnalysis'
                  )}
                </div>
              </CustomTooltip>
            }
            labelToggle={
              <IncludeInReportToggle
                included={inclusions.findings !== false}
                onToggle={(v) => handleInclusionToggle('findings', v)}
                disabled={fieldDisabled}
              />
            }
            placeholder={t(
              'activity.documentation.analysis_and_reflection.evaluationResultsPlaceholder'
            )}
            initialData={HtmlService.convertToInnerHtml({
              children: currentActivity?.analysis || '',
              richText: true,
              htmlConversionStyle: 'para',
            })}
            fieldName="analysis"
            putActivity={(type, data) => {
              putActivity(type, data);
              if (data.analysis !== undefined) {
                const ch = new BroadcastChannel('takso_activity');
                ch.postMessage({
                  type: 'analysis',
                  activityId: currentActivity.id,
                  analysis: data.analysis,
                });
                ch.close();
              }
            }}
            disabled={fieldDisabled}
          />
        )}

        <CKField
          key={`reflection-${currentActivity.id}-${
            currentActivity?.reflection || ''
          }`}
          label={
            <CustomTooltip
              state={get(user, 'options.tips')}
              contentObject={{ data: helpTips, key: '81' }}
              position="top left"
              wide="very"
            >
              <div>
                {t('activity.documentation.analysis_and_reflection.reflection')}
              </div>
            </CustomTooltip>
          }
          labelToggle={
            <IncludeInReportToggle
              included={inclusions.reflection !== false}
              onToggle={(v) => handleInclusionToggle('reflection', v)}
              disabled={fieldDisabled}
            />
          }
          placeholder={t(
            'activity.documentation.analysis_and_reflection.reflectionPlaceholder'
          )}
          initialData={HtmlService.convertToInnerHtml({
            children: currentActivity?.reflection || '',
            richText: true,
            htmlConversionStyle: 'para',
          })}
          fieldName="reflection"
          putActivity={(type, data) => {
            putActivity(type, data);
            if (data.reflection !== undefined) {
              const ch = new BroadcastChannel('takso_activity');
              ch.postMessage({
                type: 'reflection',
                activityId: currentActivity.id,
                reflection: data.reflection,
              });
              ch.close();
            }
          }}
          disabled={fieldDisabled}
        />

        <CKField
          key={`nextSteps-${currentActivity.id}-${
            currentActivity?.nextSteps || ''
          }`}
          label={
            <CustomTooltip
              state={get(user, 'options.tips')}
              contentObject={{ data: helpTips, key: '81' }}
              position="top left"
              wide="very"
            >
              <div>
                {t('activity.documentation.analysis_and_reflection.nextSteps')}
              </div>
            </CustomTooltip>
          }
          labelToggle={
            <IncludeInReportToggle
              included={inclusions.nextSteps !== false}
              onToggle={(v) => handleInclusionToggle('nextSteps', v)}
              disabled={fieldDisabled}
            />
          }
          placeholder={t(
            'activity.documentation.analysis_and_reflection.nextStepsPlaceholder'
          )}
          initialData={HtmlService.convertToInnerHtml({
            children: currentActivity?.nextSteps || '',
            richText: true,
            htmlConversionStyle: 'para',
          })}
          fieldName="nextSteps"
          putActivity={(type, data) => {
            putActivity(type, data);
            if (data.nextSteps !== undefined) {
              const ch = new BroadcastChannel('takso_activity');
              ch.postMessage({
                type: 'nextSteps',
                activityId: currentActivity.id,
                nextSteps: data.nextSteps,
              });
              ch.close();
            }
          }}
          disabled={fieldDisabled}
        />

        {showNotes && (
          <CKField
            label={
              <CustomTooltip
                state={get(user, 'options.tips')}
                contentObject={{ data: helpTips, key: '82' }}
                position="top left"
                wide="very"
              >
                <div>
                  {t(
                    'activity.documentation.analysis_and_reflection.unpublishedNote'
                  )}
                </div>
              </CustomTooltip>
            }
            placeholder=""
            initialData={HtmlService.convertToInnerHtml({
              children: currentActivity?.notes || '',
              richText: true,
              htmlConversionStyle: 'para',
            })}
            fieldName="notes"
            putActivity={putActivity}
            disabled={fieldDisabled}
            helpMessage={t(
              'activity.documentation.analysis_and_reflection.unpublishedNoteHelp'
            )}
          />
        )}
      </div>
    </Segment>
  );
}

const mapStateToProps = (state) => ({
  ...state.activity,
  user: state.authentication.user,
  helpTips: state.extra.activityHelpTips,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ putActivity }, dispatch);

const DocumentNotes = connect(
  mapStateToProps,
  mapDispatchToProps
)(DocumentNotesBase);

function FinalActivityDescriptionBase({
  currentActivity,
  putActivity,
  user,
  helpTips,
  pageDisabled,
}) {
  const { t } = useTranslation();
  const isArchived = currentActivity.statusId == 3; // eslint-disable-line eqeqeq
  const fieldDisabled = isArchived || pageDisabled;

  const parseInclusions = (raw) => {
    try {
      return JSON.parse(raw || '{}');
    } catch {
      return {};
    }
  };
  const [inclusions, setInclusions] = useState(() =>
    parseInclusions(currentActivity.reportInclusions)
  );

  useEffect(() => {
    setInclusions(parseInclusions(currentActivity.reportInclusions));
  }, [currentActivity.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wrap putActivity to also broadcast when summary is saved from this side —
  // ActivitySteps' central BroadcastChannel listener picks this up and merges
  // it into Redux, which is what lets the Report Builder preview (and this
  // field, if resaved) stay in sync regardless of which step is active.
  const handlePutActivity = (type, data) => {
    putActivity(type, data);
    if (data.summary !== undefined) {
      const ch = new BroadcastChannel('takso_activity');
      ch.postMessage({
        type: 'summary',
        activityId: currentActivity.id,
        summary: data.summary,
      });
      ch.close();
    }
  };

  const handleInclusionToggle = useCallback(
    async (key, value) => {
      setInclusions((prev) => ({ ...prev, [key]: value }));
      const merged = await saveReportInclusion({
        activityId: currentActivity.id,
        key,
        value,
      });
      if (merged) {
        setInclusions(merged);
      }
    },
    [currentActivity.id]
  );

  return (
    <div>
      <Segment className="document-note-seg" disabled={pageDisabled}>
        <div className="document-note-div">
          <CKField
            key={`summary-${currentActivity.id}-${
              currentActivity?.summary || ''
            }`}
            label={
              <div>
                {t('activity.documentation.summary.label')}{' '}
                <Popup
                  trigger={
                    <Icon name="info circle" className="doc-label-info-icon" />
                  }
                  content={t('activity.documentation.summary.tooltip')}
                  position="top left"
                  wide="very"
                />
              </div>
            }
            labelToggle={
              <IncludeInReportToggle
                included={inclusions.execSummary !== false}
                onToggle={(v) => handleInclusionToggle('execSummary', v)}
                disabled={fieldDisabled}
              />
            }
            placeholder=""
            initialData={HtmlService.convertToInnerHtml({
              children: currentActivity?.summary || '',
              richText: true,
              htmlConversionStyle: 'para',
            })}
            fieldName="summary"
            putActivity={handlePutActivity}
            disabled={fieldDisabled}
          />

          <CKField
            key={`result-${currentActivity.id}-${
              currentActivity?.result || ''
            }`}
            label={t('activity.documentation.results.label')}
            labelToggle={
              <IncludeInReportToggle
                included={inclusions.result !== false}
                onToggle={(v) => handleInclusionToggle('result', v)}
                disabled={fieldDisabled}
              />
            }
            placeholder={t('activity.documentation.results.placeholder')}
            initialData={HtmlService.convertToInnerHtml({
              children: currentActivity?.result || '',
              richText: true,
              htmlConversionStyle: 'para',
            })}
            fieldName="result"
            putActivity={(type, data) => {
              putActivity(type, data);
              if (data.result !== undefined) {
                const ch = new BroadcastChannel('takso_activity');
                ch.postMessage({
                  type: 'result',
                  activityId: currentActivity.id,
                  result: data.result,
                });
                ch.close();
              }
            }}
            disabled={fieldDisabled}
          />
        </div>
      </Segment>
    </div>
  );
}

const FinalActivityDescription = connect(
  mapStateToProps,
  mapDispatchToProps
)(FinalActivityDescriptionBase);

export { DocumentNotes, FinalActivityDescription };
